'use client';

import { c as _c } from "react/compiler-runtime";
import useSWR from 'swr';
import { useConfigsContext } from '@/context/ConfigsContext';
import { AuthenticatedCommunityIdentity, getAuthenticatedFollowCounts, getAuthenticatedFollowersWithIcon, getAuthenticatedFollowingsWithIcon, postAuthenticatedFollow, postAuthenticatedUnfollow, PublicFollowingsWithIconResults, PublicFollowingWithIcon } from '@/services/community';
import useSWRInfinite from 'swr/infinite';
export type UseFollowDataReturnType = ReturnType<typeof useFollowData>;
export type UseFollowDataFnType = Omit<UseFollowDataReturnType, 'data' | 'count'>;
const SIZE = 50;
const getFollowingsKey = (pageIndex: number, previousPageData: PublicFollowingsWithIconResults) => {
  if (pageIndex === 0 || !previousPageData) return `followings?from=0`;

  // reached the end
  if (!previousPageData.data.length) return null;
  return `followings?from=${previousPageData.from + previousPageData.size}`;
};
const getFollowersKey = (pageIndex: number, previousPageData: PublicFollowingsWithIconResults) => {
  if (pageIndex === 0 || !previousPageData) return `followers?from=0`;

  // reached the end
  if (!previousPageData.data.length) return null;
  return `followers?from=${previousPageData.from + previousPageData.size}`;
};
export function useFollowData() {
  const $ = _c(51);
  const {
    store
  } = useConfigsContext();
  const htcToken = store?.credentials?.htcToken;
  const preferredUsername = store?.profile?.userId;
  let t0;
  if ($[0] !== htcToken || $[1] !== preferredUsername) {
    t0 = htcToken && preferredUsername ? {
      htcToken,
      preferredUsername
    } : undefined;
    $[0] = htcToken;
    $[1] = preferredUsername;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const identity = t0;
  let t1;
  if ($[3] !== identity) {
    t1 = () => getAuthenticatedFollowCounts(identity);
    $[3] = identity;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    data: t2,
    mutate: mutateFollowingsCount
  } = useSWR(identity ? "getProfileFollowCounts" : null, t1);
  let t3;
  if ($[5] !== t2) {
    t3 = t2 === undefined ? {} : t2;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    followingsCount: t4,
    followersCount: t5
  } = t3;
  const followingsCount = t4 === undefined ? 0 : t4;
  const followersCount = t5 === undefined ? 0 : t5;
  let t6;
  let t7;
  if ($[7] !== identity) {
    t6 = (pageIndex, previousPageData) => identity ? getFollowingsKey(pageIndex, previousPageData) : null;
    t7 = key => {
      const query = key.split("?")[1];
      const from = Number(query.split("=")[1]);
      return getAuthenticatedFollowingsWithIcon(identity, from, SIZE);
    };
    $[7] = identity;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      revalidateFirstPage: false
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const {
    data: t9,
    mutate: mutateFollowings,
    setSize: setFollowingsSize,
    isValidating: isFollowingsValidating
  } = useSWRInfinite(t6, t7, t8);
  let t10;
  if ($[11] !== t9) {
    t10 = t9 === undefined ? [] : t9;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const followingsData = t10;
  let t11;
  let t12;
  if ($[13] !== identity) {
    t11 = (pageIndex_0, previousPageData_0) => identity ? getFollowersKey(pageIndex_0, previousPageData_0) : null;
    t12 = key_0 => {
      const query_0 = key_0.split("?")[1];
      const from_0 = Number(query_0.split("=")[1]);
      return getAuthenticatedFollowersWithIcon(identity, from_0, SIZE);
    };
    $[13] = identity;
    $[14] = t11;
    $[15] = t12;
  } else {
    t11 = $[14];
    t12 = $[15];
  }
  let t13;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = {
      revalidateFirstPage: false
    };
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  const {
    data: t14,
    setSize: setFollowersSize,
    isValidating: isFollowersValidating
  } = useSWRInfinite(t11, t12, t13);
  let t15;
  if ($[17] !== t14) {
    t15 = t14 === undefined ? [] : t14;
    $[17] = t14;
    $[18] = t15;
  } else {
    t15 = $[18];
  }
  const followersData = t15;
  let t16;
  if ($[19] !== followingsData) {
    t16 = followingsData?.flatMap(_temp);
    $[19] = followingsData;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const followingsDataMap = t16;
  let t17;
  if ($[21] !== followersData) {
    t17 = followersData?.flatMap(_temp2);
    $[21] = followersData;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const followersDataMap = t17;
  const reachedFollowingsEnd = followingsDataMap?.length === followingsCount;
  const reachedFollowersEnd = followersDataMap?.length === followersCount;
  let t18;
  if ($[23] !== followingsDataMap) {
    t18 = targetPreferredUsername => followingsDataMap.findIndex(following => following.preferredUsername === targetPreferredUsername) > -1;
    $[23] = followingsDataMap;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  const checkIfFollowing = t18;
  let t19;
  if ($[25] !== identity || $[26] !== mutateFollowings || $[27] !== mutateFollowingsCount) {
    t19 = async following_0 => {
      if (!identity) {
        throw new Error("missing authenticated community identity");
      }
      const res = await postAuthenticatedUnfollow(identity, following_0.preferredUsername);
      if (res?.ok) {
        mutateFollowingsCount();
        mutateFollowings();
      }
    };
    $[25] = identity;
    $[26] = mutateFollowings;
    $[27] = mutateFollowingsCount;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  const handleUnfollow = t19;
  let t20;
  if ($[29] !== identity || $[30] !== mutateFollowings || $[31] !== mutateFollowingsCount) {
    t20 = async following_1 => {
      if (!identity) {
        throw new Error("missing authenticated community identity");
      }
      const res_0 = await postAuthenticatedFollow(identity, following_1.preferredUsername);
      if (res_0?.ok) {
        mutateFollowingsCount();
        mutateFollowings();
      }
    };
    $[29] = identity;
    $[30] = mutateFollowings;
    $[31] = mutateFollowingsCount;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const handleRefollow = t20;
  let t21;
  if ($[33] !== followersDataMap || $[34] !== followingsDataMap) {
    t21 = {
      followings: followingsDataMap,
      followers: followersDataMap
    };
    $[33] = followersDataMap;
    $[34] = followingsDataMap;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== followersCount || $[37] !== followingsCount) {
    t22 = {
      followings: followingsCount,
      followers: followersCount
    };
    $[36] = followersCount;
    $[37] = followingsCount;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== checkIfFollowing || $[40] !== handleRefollow || $[41] !== handleUnfollow || $[42] !== isFollowersValidating || $[43] !== isFollowingsValidating || $[44] !== reachedFollowersEnd || $[45] !== reachedFollowingsEnd || $[46] !== setFollowersSize || $[47] !== setFollowingsSize || $[48] !== t21 || $[49] !== t22) {
    t23 = {
      data: t21,
      count: t22,
      reachedFollowingsEnd,
      reachedFollowersEnd,
      isFollowingsValidating,
      isFollowersValidating,
      setFollowingsSize,
      setFollowersSize,
      checkIfFollowing,
      handleUnfollow,
      handleRefollow
    };
    $[39] = checkIfFollowing;
    $[40] = handleRefollow;
    $[41] = handleUnfollow;
    $[42] = isFollowersValidating;
    $[43] = isFollowingsValidating;
    $[44] = reachedFollowersEnd;
    $[45] = reachedFollowingsEnd;
    $[46] = setFollowersSize;
    $[47] = setFollowingsSize;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
}
function _temp2(item_0) {
  return item_0?.data;
}
function _temp(item) {
  return item?.data;
}